import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add, Visibility, CheckCircle, LocalShipping, Cancel, Receipt } from '@mui/icons-material';
import procurementService from '../../services/procurement.service';
import { useLanguage } from '../../i18n/LanguageContext';

const PurchaseOrderList: React.FC = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => { loadOrders(); }, []);

  const loadOrders = async () => {
    try {
      const res = await procurementService.getPurchaseOrders();
      setOrders(res.data);
    } catch (error) { console.error('Error loading purchase orders:', error); }
    finally { setLoading(false); }
  };

  const handleStatusChange = async (id: number, status: string) => {
    const messages: Record<string, string> = {
      'APPROVED': 'Approve this purchase order?',
      'ORDERED': 'Mark as ordered (sent to supplier)?',
      'CANCELLED': 'Cancel this purchase order?'
    };
    if (!window.confirm(messages[status] || `Change status to ${status}?`)) return;
    try {
      await procurementService.updatePOStatus(id, status);
      loadOrders();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error updating status');
    }
  };

  const handleGenerateInvoice = async (id: number, orderNumber: string) => {
    if (!window.confirm(`Generate bill from ${orderNumber}?`)) return;
    try {
      const res = await procurementService.generateInvoiceFromPO(id);
      alert(res.data.message);
      navigate('/purchase-invoices');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error generating bill');
    }
  };

  const getStatusColor = (status: string): "default" | "warning" | "info" | "primary" | "success" | "error" => {
    const colors: Record<string, any> = {
      'DRAFT': 'warning', 'APPROVED': 'info', 'ORDERED': 'primary',
      'RECEIVED': 'success', 'CANCELLED': 'error'
    };
    return colors[status] || 'default';
  };

  const columns: GridColDef[] = [
    { field: 'orderNumber', headerName: t('purchase_orders.po_number'), width: 200 },
    {
      field: 'orderDate', headerName: t('common.date'), width: 120,
      renderCell: (params: GridRenderCellParams) => new Date(params.value).toLocaleDateString()
    },
    { field: 'supplierName', headerName: t('purchase_orders.supplier'), flex: 1 },
    {
      field: 'status', headerName: t('common.status'), width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value} color={getStatusColor(params.value as string)} size="small" />
      )
    },
    {
      field: 'totalAmount', headerName: t('common.total'), width: 130, align: 'right', headerAlign: 'right',
      renderCell: (params: GridRenderCellParams) => {
        const curr = params.row.currencyCode || 'USD';
        return `${Number(params.value).toFixed(2)} ${curr}`;
      }
    },
    {
      field: 'expectedDeliveryDate', headerName: t('purchase_orders.expected_delivery'), width: 150,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? new Date(params.value).toLocaleDateString() : '—'
    },
    { field: 'lineCount', headerName: t('purchase_orders.items'), width: 80, align: 'center' },
    {
      field: 'actions', headerName: t('common.actions'), width: 200, sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title={t('common.view')}>
            <IconButton size="small" onClick={() => navigate(`/purchase-orders/${params.row.id}`)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status === 'DRAFT' && (
            <>
              <Tooltip title={t('purchase_orders.approve')}>
                <IconButton size="small" color="info" onClick={() => handleStatusChange(params.row.id, 'APPROVED')}>
                  <CheckCircle fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('common.cancel')}>
                <IconButton size="small" color="error" onClick={() => handleStatusChange(params.row.id, 'CANCELLED')}>
                  <Cancel fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {params.row.status === 'APPROVED' && (
            <>
              <Tooltip title={t('purchase_orders.mark_ordered')}>
                <IconButton size="small" color="primary" onClick={() => handleStatusChange(params.row.id, 'ORDERED')}>
                  <LocalShipping fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('purchase_orders.generate_bill')}>
                <IconButton size="small" color="secondary" onClick={() => handleGenerateInvoice(params.row.id, params.row.orderNumber)}>
                  <Receipt fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {(params.row.status === 'ORDERED' || params.row.status === 'RECEIVED') && (
            <Tooltip title={t('purchase_orders.generate_bill')}>
              <IconButton size="small" color="secondary" onClick={() => handleGenerateInvoice(params.row.id, params.row.orderNumber)}>
                <Receipt fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t('purchase_orders.title')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/purchase-orders/new')}>
          {t('purchase_orders.new')}
        </Button>
      </Box>
      <DataGrid rows={orders} columns={columns} loading={loading} autoHeight
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        pageSizeOptions={[25, 50]} disableRowSelectionOnClick />
    </Box>
  );
};

export default PurchaseOrderList;
